import { useEffect, useRef, useState } from "react";
import { Check, Plus } from "lucide-react";
import { cn } from "@/utils/helpers";
import { KitButton } from "../../components/kit/Button";
import { KitModal } from "../../components/kit/Modal";
import PlatformGlyph from "../../components/PlatformGlyph";
import { channelName, durationLabel } from "./liveModel";
import { LIVE_ACCEPT_IMAGES } from "./liveStage";
import type { LiveChannel, LiveFile } from "./liveTypes";

/**
 * Cover for a video channel (Figma 08, 6 Live): "Pick a frame" slides along the
 * video and shows how the profile grid crops it; "Use a picture" picks one of
 * the post's pictures or adds one, which joins the files. TikTok takes a frame
 * only; YouTube a picture only; an Instagram Reel either.
 */

export type CoverChoice =
  | { kind: "frame"; ms: number }
  | { kind: "picture"; fileId: string }
  /** Let TikTok choose: no frame is set, the platform picks one when it posts. */
  | { kind: "auto" };

const STRIP_FRAMES = 12;

/** "0:03" from ms. */
function at(ms: number): string {
  return durationLabel(Math.round(ms / 1000)) ?? "0:00";
}

function sizeLabel(bytes: number | null | undefined): string | null {
  if (!bytes) return null;
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** The frames along the video, drawn once from one hidden copy of it. */
function FilmStrip({ url, durationMs, value, onChange }: { url: string; durationMs: number; value: number; onChange: (ms: number) => void }) {
  const canvases = useRef<Array<HTMLCanvasElement | null>>([]);
  const track = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!durationMs) return;
    let cancelled = false;
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = url;
    const seek = (seconds: number) =>
      new Promise<void>((resolve) => {
        const done = () => {
          video.removeEventListener("seeked", done);
          resolve();
        };
        video.addEventListener("seeked", done);
        video.currentTime = seconds;
      });
    const draw = async () => {
      for (let index = 0; index < STRIP_FRAMES && !cancelled; index++) {
        await seek(((index + 0.5) / STRIP_FRAMES) * (durationMs / 1000));
        const canvas = canvases.current[index];
        const context = canvas?.getContext("2d");
        // Drawing a frame only shows it; nothing reads the pixels back.
        if (canvas && context) context.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
    };
    video.addEventListener("loadeddata", () => void draw(), { once: true });
    return () => {
      cancelled = true;
      video.removeAttribute("src");
      video.load();
    };
  }, [url, durationMs]);

  const pick = (clientX: number) => {
    const box = track.current?.getBoundingClientRect();
    if (!box || !durationMs) return;
    const ratio = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    onChange(Math.round(ratio * durationMs));
  };
  const share = durationMs ? Math.min(1, value / durationMs) : 0;

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={track}
        role="slider"
        tabIndex={0}
        aria-label="Slide to a frame"
        aria-valuemin={0}
        aria-valuemax={durationMs}
        aria-valuenow={value}
        aria-valuetext={at(value)}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
          pick(event.clientX);
        }}
        onPointerMove={(event) => dragging && pick(event.clientX)}
        onPointerUp={() => setDragging(false)}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 1000 : 100;
          if (event.key === "ArrowLeft") onChange(Math.max(0, value - step));
          else if (event.key === "ArrowRight") onChange(Math.min(durationMs, value + step));
        }}
        className="relative flex h-[100px] cursor-pointer touch-none select-none overflow-hidden rounded-[8px] bg-co-recess outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
      >
        {Array.from({ length: STRIP_FRAMES }, (_, index) => (
          <canvas
            key={index}
            ref={(node) => {
              canvases.current[index] = node;
            }}
            width={56}
            height={100}
            className="h-full min-w-0 flex-1 object-cover"
          />
        ))}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 w-[60px] rounded-[8px] shadow-[inset_0_0_0_2px_var(--co-accent)]"
          style={{ left: `calc(${share * 100}% - ${share * 60}px)` }}
        />
      </div>
      <div className="flex justify-between text-[12.5px] tabular-nums text-co-muted">
        <span>0:00</span>
        <span>{at(durationMs)}</span>
      </div>
    </div>
  );
}

export default function LiveCoverDialog({
  channel,
  video,
  pictures,
  frameMs,
  coverFileId,
  onConfirm,
  onAddPicture,
  onClose,
}: {
  channel: LiveChannel;
  /** The video this channel posts. */
  video: LiveFile;
  /** The pictures in this post's files. */
  pictures: LiveFile[];
  /** The frame set now, in ms; null when none. */
  frameMs: number | null;
  /** The cover picture set now, when there is one. */
  coverFileId: string | null;
  onConfirm: (choice: CoverChoice) => void;
  /** Uploads a picture into the post's files. */
  onAddPicture?: (file: File) => void;
  onClose: () => void;
}) {
  const tiktok = channel.platform === "TIKTOK";
  const youtube = channel.platform === "YOUTUBE";
  // TikTok: pick a frame, or let TikTok choose (Figma 08, 13 Oct). YouTube: a picture only.
  const tabs: Array<"frame" | "picture" | "auto"> = tiktok ? ["frame", "auto"] : youtube ? ["picture"] : ["frame", "picture"];
  const [tab, setTab] = useState<"frame" | "picture" | "auto">(
    coverFileId && tabs.includes("picture") ? "picture" : tiktok && frameMs === null ? "auto" : tabs[0]!,
  );
  const [durationMs, setDurationMs] = useState(Math.round((video.durationSeconds ?? 0) * 1000));
  const [ms, setMs] = useState(frameMs ?? 0);
  const [picked, setPicked] = useState<string | null>(coverFileId);
  const preview = useRef<HTMLVideoElement | null>(null);
  const crop = useRef<HTMLVideoElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const name = channelName(channel);
  const title = tiktok ? "Cover for TikTok" : `Cover for the ${name}`;
  const platform = name.split(" ")[0];
  const description = tiktok
    ? "Pick a frame of the video, or let TikTok choose. TikTok takes a cover only as a frame of the video."
    : youtube
      ? "Use a picture from this post’s files or a new one. YouTube shows it as the thumbnail."
      : `Pick a frame of the video, or use a picture from this post’s files or a new one. ${platform} shows it on the Reels tab and the grid.`;

  useEffect(() => {
    for (const node of [preview.current, crop.current]) {
      if (node && Math.abs(node.currentTime - ms / 1000) > 0.04) node.currentTime = ms / 1000;
    }
  }, [ms, tab]);

  const pickedPicture = pictures.find((file) => file.id === picked) ?? null;
  const offShape = pickedPicture?.ratio && pickedPicture.ratio !== "9:16" ? pickedPicture.ratio : null;
  const canConfirm = tab === "frame" ? !!video.mediaUrl : tab === "auto" ? true : !!pickedPicture;

  return (
    <KitModal isOpen onOpenChange={(open) => !open && onClose()} title={title} description={description} modalClassName="flex max-h-[90dvh] max-w-[720px] flex-col">
      {tabs.length > 1 ? (
        <div role="tablist" aria-label="What the cover is" className="mb-4 flex h-10 w-fit shrink-0 rounded-pill bg-co-tonal p-[3px]">
          {tabs.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                "rounded-pill px-3.5 text-[13.5px] outline-none transition-colors focus-visible:outline-2 focus-visible:outline-co-accent",
                tab === value ? "bg-co-hover font-semibold text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)]" : "text-co-text2 hover:text-co-text",
              )}
            >
              {value === "frame" ? "Pick a frame" : value === "auto" ? "Let TikTok choose" : "Use a picture"}
            </button>
          ))}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {tab === "auto" ? (
          // Let TikTok choose (Figma 08, 13 Oct): nothing to set here.
          <div className="flex flex-col gap-3 rounded-card bg-co-recess p-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted">Chosen by TikTok · when the video goes out</p>
            <p className="text-[16px] font-semibold leading-snug text-co-text">TikTok picks the cover</p>
            <p className="text-[14px] leading-relaxed text-co-text2">
              TikTok chooses a frame from the video itself when it goes out. There is nothing to set here.
            </p>
            <p className="text-[13px] leading-relaxed text-co-muted">
              You see the cover on TikTok once the video is live. To decide it yourself, choose Pick a frame.
            </p>
          </div>
        ) : tab === "frame" ? (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap gap-6">
              <div className="flex flex-col gap-2">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted">Frame at {at(ms)}</p>
                <div className="relative h-[400px] w-[225px] overflow-hidden rounded-[10px] bg-co-recess">
                  <video
                    ref={preview}
                    src={video.mediaUrl ?? undefined}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(event) => {
                      const seconds = event.currentTarget.duration;
                      if (Number.isFinite(seconds) && seconds > 0) setDurationMs(Math.round(seconds * 1000));
                      event.currentTarget.currentTime = ms / 1000;
                    }}
                    className="size-full object-cover"
                  />
                  <span className="absolute left-3 top-3 rounded-[6px] bg-black/60 px-2 py-0.5 text-[12.5px] tabular-nums text-white">{at(ms)}</span>
                  <span className="absolute right-3 top-3 text-white">
                    <PlatformGlyph platform={channel.platform} className="size-4" />
                  </span>
                  {/* The profile grid keeps the middle 3:4 of a 9:16 frame. */}
                  <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[12.5%] border-b border-dashed border-white/80 bg-black/35" />
                  <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[12.5%] border-t border-dashed border-white/80 bg-black/35" />
                </div>
              </div>
              <div className="flex w-[240px] flex-col gap-2">
                <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted">On your profile · 3:4</p>
                <div className="h-[320px] w-[240px] overflow-hidden rounded-[10px] bg-co-recess">
                  <video ref={crop} src={video.mediaUrl ?? undefined} muted playsInline preload="metadata" className="size-full object-cover" />
                </div>
                <p className="text-[12.5px] leading-[1.45] text-co-text2">
                  Your {platform} profile grid shows the middle of the frame. The dimmed top and bottom are cut.
                </p>
              </div>
            </div>
            {video.mediaUrl && durationMs ? (
              <div className="flex flex-col gap-2">
                <div className="flex justify-between font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted">
                  <span>Slide to a frame</span>
                  <span className="tabular-nums">
                    {at(ms)} of {at(durationMs)}
                  </span>
                </div>
                <FilmStrip url={video.mediaUrl} durationMs={durationMs} value={ms} onChange={setMs} />
              </div>
            ) : (
              <p className="text-[13px] text-co-muted">The video is still being saved. Its frames show here once it is in.</p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted">Pictures in this post’s files · {pictures.length}</p>
            <div className="flex flex-wrap gap-3">
              {pictures.map((file) => {
                const on = picked === file.id;
                return (
                  <button
                    key={file.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setPicked(file.id)}
                    className="flex w-[118px] flex-col gap-1.5 text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
                  >
                    <span className={cn("relative block h-[210px] w-[118px] overflow-hidden rounded-[8px] bg-co-recess", on && "shadow-[0_0_0_2px_var(--co-accent)]")}>
                      {file.thumbUrl ?? file.mediaUrl ? <img src={(file.thumbUrl ?? file.mediaUrl)!} alt="" className="size-full object-cover" /> : null}
                      {on ? (
                        <span className="absolute right-2 top-2 grid size-[18px] place-items-center rounded-[4px] bg-co-accent text-co-on-accent">
                          <Check className="size-3" strokeWidth={2.6} aria-hidden="true" />
                        </span>
                      ) : null}
                    </span>
                    <span className="truncate text-[13px] text-co-text">{file.name}</span>
                    <span className="font-mono text-[10px] uppercase leading-[13px] tracking-[0.16em] text-co-muted">
                      <span className="block">Picture{file.ratio ? ` ${file.ratio}` : ""}</span>
                      {sizeLabel(file.sizeBytes) ? <span className="block">{sizeLabel(file.sizeBytes)}</span> : null}
                    </span>
                  </button>
                );
              })}
              {onAddPicture ? (
                <div className="flex w-[118px] flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    className="flex h-[210px] w-[118px] flex-col items-center justify-center gap-2 rounded-[8px] border border-dashed border-co-faint text-[13px] text-co-text2 outline-none hover:border-co-text2 focus-visible:outline-2 focus-visible:outline-co-accent"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    Add a picture
                  </button>
                  <span className="text-[12.5px] leading-[1.4] text-co-muted">JPEG or PNG. It goes into the files.</span>
                  <input
                    ref={fileInput}
                    type="file"
                    accept={LIVE_ACCEPT_IMAGES}
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      if (file) onAddPicture(file);
                    }}
                  />
                </div>
              ) : null}
            </div>
            <p className="text-[13px] text-co-text2">
              {offShape
                ? `This picture is ${offShape}. ${platform} cuts it to fill the 9:16 frame.`
                : "A picture you add here joins the files, so it can be used again."}
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 flex shrink-0 flex-wrap items-center gap-3">
        {tab === "frame" ? (
          <span className="mr-auto truncate text-[13px] text-co-text2">
            {[video.name, video.ratio, durationLabel(video.durationSeconds)].filter(Boolean).join(" · ")}
          </span>
        ) : (
          <span className="mr-auto" />
        )}
        <KitButton variant="ghost" onClick={onClose}>
          Cancel
        </KitButton>
        <KitButton
          isDisabled={!canConfirm}
          onClick={() => {
            if (tab === "frame") onConfirm({ kind: "frame", ms: Math.round(ms) });
            else if (tab === "auto") onConfirm({ kind: "auto" });
            else if (picked) onConfirm({ kind: "picture", fileId: picked });
            onClose();
          }}
        >
          {tab === "frame" ? "Use this frame" : tab === "auto" ? "Let TikTok choose" : "Use this picture"}
        </KitButton>
      </div>
    </KitModal>
  );
}
